import { motion, useScroll, useTransform } from "framer-motion";
import { Award, Shield, Zap, Users, CheckCircle, Star, Target, Lightbulb, Sparkles } from "lucide-react";
import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";
import { useTranslation } from "@/hooks/useTranslation";
import { useRef } from "react";

const WhyUs = () => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360]);
  
  const advantages = [
    {
      icon: Award,
      backgroundColor: "#A9E0DD",
      iconColor: "#A9E0DD",
      textColor: "text-slate-800"
    },
    {
      icon: Target,
      backgroundColor: "#66B3BB",
      iconColor: "#66B3BB",
      textColor: "text-slate-800"
    },
    {
      icon: Shield,
      backgroundColor: "#C8DDFF",
      iconColor: "#C8DDFF",
      textColor: "text-slate-800"
    },
    {
      icon: CheckCircle,
      backgroundColor: "#868EAD",
      iconColor: "#868EAD",
      textColor: "text-slate-800"
    },
    {
      icon: Users,
      backgroundColor: "#B4C3FD",
      iconColor: "#B4C3FD",
      textColor: "text-slate-800"
    },
    {
      icon: Zap,
      backgroundColor: "#70989c",
      iconColor: "#70989c",
      textColor: "text-slate-800"
    }
  ];
  return <div ref={containerRef} className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50 relative overflow-hidden">
      <Navigation />
      
      {/* Flowing background elements */}
      <div className="absolute top-0 right-0 w-1/2 h-[60%] bg-gradient-to-bl from-secondary/15 via-secondary/8 to-transparent blur-3xl" />
      <div className="absolute top-1/4 left-0 w-1/3 h-[50%] bg-gradient-to-r from-accent-teal/12 via-accent-teal/6 to-transparent blur-2xl" />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-[40%] bg-gradient-to-t from-secondary/10 via-transparent to-transparent blur-3xl" />

      <main className="pt-32 pb-16 relative z-10">
        
        <div className="container-padding relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Hero Section */}
            <motion.div className="text-center mb-20" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }}>
              <motion.div className="inline-flex items-center gap-2 bg-gradient-to-r from-secondary/20 to-accent-teal/20 
                           border border-secondary/30 rounded-full px-8 py-4 text-secondary font-semibold
                           backdrop-blur-lg mb-8 shadow-lg" initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} whileHover={{
              scale: 1.05,
              boxShadow: "0 20px 40px -10px rgba(0,0,0,0.1)"
            }}>
                <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}>
                  <Star className="w-5 h-5" />
                </motion.div>
                {t.whyUs.badge}
              </motion.div>
              
              <div className="relative">
                <motion.h1 initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.8,
                delay: 0.3
              }} className="heading-xl mb-6 py-[10px] relative text-accent-teal">
                  <span className="bg-gradient-to-r from-accent-teal via-primary to-secondary bg-clip-text text-accent-teal">
                    {t.whyUs.title}
                  </span>
                  
                  {/* Animated underline */}
                  
                </motion.h1>
                
                {/* Floating accent elements around the title */}
                <motion.div className="absolute -top-4 -right-8 w-6 h-6 bg-secondary/20 rounded-full" animate={{
                y: [0, -10, 0],
                opacity: [0.3, 0.8, 0.3]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                delay: 1
              }} />
                <motion.div className="absolute -bottom-4 -left-6 w-4 h-4 bg-accent-teal/20 rounded-full" animate={{
                y: [0, 8, 0],
                opacity: [0.4, 0.9, 0.4]
              }} transition={{
                duration: 3.5,
                repeat: Infinity,
                delay: 1.5
              }} />
              </div>
              
              <motion.p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.5
            }}>
                {t.whyUs.subtitle}
              </motion.p>
            </motion.div>

            {/* Advantages Bento Grid */}
            <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-32 relative" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.7
          }}>
              {advantages.map((advantage, index) => {
              // Define special layouts for specific cards
              const isLarge = index === 0 || index === 3; // First and fourth cards span 2 columns
              const isTall = index === 2; // Third card is taller

              return <motion.div key={index} className={`relative group ${isLarge ? 'md:col-span-2' : ''} ${isTall ? 'md:row-span-2' : ''}`} initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.6,
                delay: 0.8 + index * 0.1,
                type: "spring",
                stiffness: 100
              }} whileHover={{
                scale: 1.02,
                y: -8
              }}>
                    {/* Card with colored background */}
                    <div className="relative rounded-3xl p-8 shadow-xl border border-white/40 hover:shadow-2xl transition-all duration-500 ease-out h-full overflow-hidden backdrop-blur-sm" style={{
                  backgroundColor: advantage.backgroundColor
                }}>
                      {/* Decorative gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-40" />
                      <div className="absolute inset-0 bg-gradient-to-tl from-white/5 via-transparent to-transparent" />
                      
                      {/* Decorative circle */}
                      <motion.div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-20" style={{
                    backgroundColor: advantage.iconColor
                  }} animate={{
                    scale: [1, 1.2, 1],
                    rotate: [0, 90, 0]
                  }} transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }} />
                      
                      {/* Content container */}
                      <div className="relative z-10 flex flex-col h-full">
                        {/* Icon */}
                        <motion.div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-white/40 backdrop-blur-sm shadow-lg flex items-center justify-center mb-6" whileHover={{
                      scale: 1.1,
                      rotate: 5
                    }} transition={{
                      type: "spring",
                      stiffness: 400,
                      damping: 15
                    }}>
                          <advantage.icon className="w-10 h-10 text-white" />
                        </motion.div>
                        
                        {/* Text content */}
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold mb-4 leading-tight drop-shadow-md text-slate-800">
                            {t.whyUs.advantages[index].title}
                          </h3>
                          <p className="leading-relaxed drop-shadow-sm text-slate-800">
                            {t.whyUs.advantages[index].description}
                          </p>
                        </div>
                      </div>
                      
                      {/* Hover glow effect */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl" style={{
                    boxShadow: `inset 0 0 60px ${advantage.iconColor}40`
                  }} />
                    </div>
                  </motion.div>;
            })}
            </motion.div>

            {/* UN Global Compact Section */}
            <motion.div className="mb-32 relative" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.8
          }}>
              <div className="bg-white rounded-2xl shadow-lg border-4 border-secondary/20 p-8 md:p-12 max-w-3xl mx-auto py-[22px]">
                {/* Header with logo */}
                <div className="text-center mb-8 my-0">
                  <motion.div initial={{
                  scale: 0.8,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  delay: 0.9,
                  duration: 0.5
                }} className="inline-block mb-6">
                    <img src="/lovable-uploads/un-global-compact-logo.webp" alt="UN Global Compact logo" className="w-40 h-40 object-contain mx-auto" />
                  </motion.div>
                  
                  <h2 className="text-3xl font-bold text-slate-800 mb-3 md:text-4xl">{t.whyUs.unGlobalCompact.title}</h2>
                  
                </div>

                {/* Main content */}
                <p className="text-slate-700 text-center leading-relaxed mb-8 text-base">{t.whyUs.unGlobalCompact.description}</p>

                {/* Four pillars grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {[{
                  title: t.whyUs.unGlobalCompact.pillars.humanRights,
                  icon: Users,
                  bgColor: "#A9E0DD"
                }, {
                  title: t.whyUs.unGlobalCompact.pillars.labour,
                  icon: Target,
                  bgColor: "#66B3BB"
                }, {
                  title: t.whyUs.unGlobalCompact.pillars.environment,
                  icon: Lightbulb,
                  bgColor: "#C8DDFF"
                }, {
                  title: t.whyUs.unGlobalCompact.pillars.antiCorruption,
                  icon: Shield,
                  bgColor: "#868EAD"
                }].map((pillar, idx) => <motion.div key={pillar.title} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 1 + idx * 0.1,
                  duration: 0.5
                }} className="text-center p-4 rounded-xl border border-white/40 shadow-lg" style={{
                  backgroundColor: pillar.bgColor
                }}>
                      <pillar.icon className="w-8 h-8 mx-auto mb-2 text-white" />
                      <p className="text-xs sm:text-sm font-extrabold text-zinc-50 break-words hyphens-auto leading-snug px-2" style={{ wordBreak: 'break-word' }}>{pillar.title}</p>
                    </motion.div>)}
                </div>

                {/* Footer statement */}
                
              </div>
            </motion.div>

            {/* Final Statement */}
            <motion.div className="healthcare-card group relative overflow-hidden" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 1.0
          }}>
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent-teal/5 rounded-2xl" />
              <div className="relative z-10">
                <div className="flex items-center justify-center gap-4 mb-8">
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-secondary to-accent-teal text-white">
                    <Sparkles className="w-10 h-10" />
                  </div>
                  <h2 className="text-4xl font-bold text-slate-800 py-[10px]">{t.whyUs.finalStatement.title}</h2>
                </div>
                <p className="text-xl text-slate-700 leading-relaxed max-w-4xl mx-auto py-[2px] text-center">
                  {t.whyUs.finalStatement.description}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
      <Footer />
    </div>;
};
export default WhyUs;